import type { ReactElement } from 'react';

import { useTranslation } from 'react-i18next';

import { Spend } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import type { CompanyQuota, Quota } from 'domain/quota';
import { useIsDataCreditsEnabled } from 'features/quota/hooks/useIsDataCreditsEnabled';
import { formatCompactNumber } from 'services/numberFormat';

import {
  StyledContactsIcon,
  StyledCreditCount,
  StyledCreditCountContainer,
  StyledCreditItem,
  StyledCreditsBlock,
  StyledCreditSubTitle,
  StyledCreditTitleContainer,
  StyledInsightsIcon,
  StyledOverviewIcon,
  StyledSpendIcon,
} from './styles';

interface Props {
  insightsQuota: Quota;
  companyQuota?: CompanyQuota;
  quota: Quota;
}

export const QuotaBlock = ({ insightsQuota, companyQuota, quota }: Props): ReactElement => {
  const { isDataCreditsEnabled } = useIsDataCreditsEnabled();

  const { t } = useTranslation();

  const overviewLabel = () => {
    if (!companyQuota || companyQuota?.interval === 'unlimited') {
      return null;
    }

    return (
      <StyledCreditSubTitle>{t(`settings.credits.overview.label.${companyQuota?.interval}`)}</StyledCreditSubTitle>
    );
  };

  const renderQuotaText = (used: number, total: number): string =>
    `${formatCompactNumber(used)}/${formatCompactNumber(total)}`;

  return (
    <StyledCreditsBlock>
      {companyQuota && !companyQuota.isUnlimited && !isDataCreditsEnabled ? (
        <StyledCreditItem>
          <StyledOverviewIcon iconColor={colorsPalettes.carbon[500]} iconName="search" size="xs" />
          <StyledCreditTitleContainer>
            {t('settings.credits.overview.title')}
            {overviewLabel()}
          </StyledCreditTitleContainer>

          <StyledCreditCount>{renderQuotaText(companyQuota?.used, companyQuota?.total)}</StyledCreditCount>
        </StyledCreditItem>
      ) : null}

      <StyledCreditItem>
        {isDataCreditsEnabled ? (
          <>
            <StyledSpendIcon size={16} color={colorsPalettes.carbon[400]} />
            <StyledCreditTitleContainer>
              {t('settings.credits.settings.credits.data-credits')}
              <StyledCreditSubTitle>Monthly</StyledCreditSubTitle>
            </StyledCreditTitleContainer>
          </>
        ) : (
          <>
            <StyledContactsIcon iconName="contacts" size="xs" />
            <StyledCreditTitleContainer>
              {t('settings.credits.contact')}
              <StyledCreditSubTitle>Monthly</StyledCreditSubTitle>
            </StyledCreditTitleContainer>
          </>
        )}

        {isDataCreditsEnabled ? (
          <StyledCreditCountContainer>
            <Spend size={16} color={colorsPalettes.carbon[400]} />

            <StyledCreditCount>{renderQuotaText(quota.used, quota.total)}</StyledCreditCount>
          </StyledCreditCountContainer>
        ) : (
          <StyledCreditCount>{renderQuotaText(quota.used, quota.total)}</StyledCreditCount>
        )}
      </StyledCreditItem>

      <StyledCreditItem>
        <StyledInsightsIcon iconColor={colorsPalettes.carbon[500]} iconName="sparkles-ai" size="xs" />
        <StyledCreditTitleContainer>
          {t('settings.credits.insights')}
          <StyledCreditSubTitle>Monthly</StyledCreditSubTitle>
        </StyledCreditTitleContainer>

        <StyledCreditCount>{renderQuotaText(insightsQuota.used, insightsQuota.total)}</StyledCreditCount>
      </StyledCreditItem>
    </StyledCreditsBlock>
  );
};
